// Dashboard — "Aufgaben zuerst" (Basware/SAP-DRC-Muster aus der UX-Recherche):
// oben klickbare Aufgaben-KPIs, darunter Volumen-Trend (12 Monate, 2 Serien)
// + Status-Funnel, unten Regime-Abdeckung und letzte Aktivitaet.
//
// Chart-Serienfarben: dataviz-validierte Palette (beide Modi geprueft):
//   Eingang  hell #3b82f6 / dunkel #3b82f6   (blue-500)
//   Ausgang  hell #f59e0b / dunkel #d97706   (amber-500 / amber-600)
// Amber traegt im Hellmodus eine Kontrast-Auflage -> Legende + direkte Wertelabels
// sind deshalb Pflichtbestandteil, nicht Deko. CSS-Balken wie im Lager-Dashboard.

import React, { useCallback, useEffect, useState } from 'react';
import { Inbox, Send, AlertTriangle, CalendarClock, RefreshCw } from 'lucide-react';
import { Card, SectionTitle, KpiTile, BusinessChip, money, fmtDate, btnGhost } from './ui';
import PlantFilter from './PlantFilter';
import { getDashboard } from '../api';

const SERIES = {
  inbound: { light: '#3b82f6', dark: '#3b82f6' },
  outbound: { light: '#f59e0b', dark: '#d97706' },
};

const DashboardScreen = ({ user, isDarkMode, t, regimes, plants = [], onOpenList, onOpenInvoice, showError }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plant, setPlant] = useState(''); // '' = Alle Werke

  const load = useCallback(async () => {
    setLoading(true);
    try { setStats(await getDashboard(user, plant)); }
    catch (e) { showError?.(t('einvoicing.dashLoadError') + ': ' + e.message); }
    finally { setLoading(false); }
  }, [user, plant, showError, t]);
  useEffect(() => { load(); }, [load]);

  const count = (dir, states) => (stats?.byState || [])
    .filter((r) => r.direction === dir && (!states || states.includes(r.state)))
    .reduce((s, r) => s + Number(r.n), 0);

  const labelMuted = isDarkMode ? 'text-white/55' : 'text-slate-500';
  const textMain = isDarkMode ? 'text-white/80' : 'text-slate-700';

  // --- Volumen-Chart: 12 Monate x 2 Serien als gruppierte CSS-Balken ---
  const months = [];
  {
    const byMonth = new Map();
    for (const r of stats?.monthly || []) {
      const m = byMonth.get(r.month) || { month: r.month, inbound: 0, outbound: 0 };
      m[r.direction] = Number(r.n);
      byMonth.set(r.month, m);
    }
    months.push(...[...byMonth.values()].sort((a, b) => a.month.localeCompare(b.month)));
  }
  const maxN = Math.max(1, ...months.flatMap((m) => [m.inbound, m.outbound]));
  const BAR_H = 120;
  const mode = isDarkMode ? 'dark' : 'light';

  // --- Status-Funnel Eingang (SAP-DRC-Muster: complete / in process / error) ---
  const funnel = [
    { key: 'in_process', label: t('einvoicing.funnelInProcess'), n: count('inbound', ['received', 'validated', 'in_approval', 'approved']), tone: 'blue' },
    { key: 'error', label: t('einvoicing.funnelError'), n: count('inbound', ['validation_failed', 'rejected']), tone: 'red' },
    { key: 'complete', label: t('einvoicing.funnelComplete'), n: count('inbound', ['posted_to_sap', 'archived']), tone: 'green' },
  ];
  const funnelMax = Math.max(1, ...funnel.map((f) => f.n));
  const funnelBar = { blue: 'bg-blue-500', red: 'bg-red-500', green: 'bg-emerald-500' };

  return (
    <div className="space-y-6">
      {/* Werksfilter — scopet KPIs, Charts und Recent (Backend erzwingt zusaetzlich die Berechtigung) */}
      {plants.length > 0 && (
        <div className="flex justify-end">
          <PlantFilter plants={plants} value={plant} onChange={setPlant} isDarkMode={isDarkMode} t={t} />
        </div>
      )}

      {/* Aufgaben-KPIs — klickbar, filtern die Liste */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile isDarkMode={isDarkMode} Icon={Inbox} tone="amber"
          label={t('einvoicing.kpiNeedsAction')}
          value={count('inbound', ['received', 'validation_failed', 'in_approval'])}
          onClick={() => onOpenList('inbound', { needsAction: true })} />
        <KpiTile isDarkMode={isDarkMode} Icon={AlertTriangle} tone="red"
          label={t('einvoicing.kpiValidationFailed')}
          value={count('inbound', ['validation_failed'])}
          onClick={() => onOpenList('inbound', { state: 'validation_failed' })} />
        <KpiTile isDarkMode={isDarkMode} Icon={CalendarClock} tone="blue"
          label={t('einvoicing.kpiDueSoon')}
          value={stats?.dueSoonCount ?? '—'}
          onClick={() => onOpenList('inbound', {})} />
        <KpiTile isDarkMode={isDarkMode} Icon={Send} tone="slate"
          label={t('einvoicing.kpiOutboundOpen')}
          value={count('outbound', ['draft', 'generated'])}
          onClick={() => onOpenList('outbound', {})} />
      </div>

      {/* grid-cols-1 explizit: Tailwind-Spalten sind minmax(0,1fr) (Min 0) -> volle Breite UND
          schrumpfbar. Ohne das faellt Mobil auf eine implizite auto-Spalte zurueck, die auf die
          Chart-Breite waechst und die Seite horizontal sprengt. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Volumen-Trend — min-w-0: als Grid-Item sonst min-width:auto -> die Card waechst auf die
            volle Balkenbreite (~12 Monate) statt den Chart in seinem overflow-x-auto-Kasten scrollen
            zu lassen; genau das sprengt die Seite auf dem Smartphone ~2x breit. */}
        <Card isDarkMode={isDarkMode} className="min-w-0">
          <SectionTitle isDarkMode={isDarkMode}
            right={<button onClick={load} className={btnGhost(isDarkMode)} title={t('common.refresh')}><RefreshCw className="w-3.5 h-3.5" /></button>}>
            {t('einvoicing.dashVolume')}
          </SectionTitle>
          {months.length === 0 ? (
            <p className={`text-sm ${labelMuted}`}>{loading ? t('einvoicing.loading') : t('einvoicing.dashEmpty')}</p>
          ) : (
            <>
              {/* Alle 12 Monate fuellen die Breite (flex-1) statt horizontal zu scrollen ->
                  kein Abschneiden. Duennere Balken auf dem Smartphone (w-2), volle Breite ab sm. */}
              <div className="flex items-end gap-1 pb-1" style={{ minHeight: BAR_H + 30 }}>
                {months.map((m) => (
                  <div key={m.month} className="flex flex-col items-center flex-1 min-w-0">
                    <div className="flex items-end gap-0.5" style={{ height: BAR_H }}>
                      {['inbound', 'outbound'].map((dir) => (
                        <div key={dir} className="relative group flex flex-col justify-end" style={{ height: BAR_H }}>
                          <div
                            className="w-2 sm:w-4 rounded-t-[4px] transition-all"
                            style={{ height: Math.max(2, (m[dir] / maxN) * BAR_H), backgroundColor: SERIES[dir][mode] }}
                            title={`${m.month} · ${t(dir === 'inbound' ? 'einvoicing.inbound' : 'einvoicing.outbound')}: ${m[dir]}`}
                          />
                          {/* Werte per Hover ab sm — bei 12 schmalen Balken wuerden sie sich auf dem Smartphone ueberlappen */}
                          <span className={`absolute -top-4 left-1/2 -translate-x-1/2 text-[10px] tabular-nums opacity-0 sm:group-hover:opacity-100 ${textMain}`}>{m[dir]}</span>
                        </div>
                      ))}
                    </div>
                    {/* Achse: auf dem Smartphone nur der Monat (MM), damit alle 12 passen; ab sm YY/MM */}
                    <span className={`text-[11px] mt-1 leading-none ${labelMuted}`}>
                      <span className="sm:hidden">{m.month.slice(5)}</span>
                      <span className="hidden sm:inline">{m.month.slice(2).replace('-', '/')}</span>
                    </span>
                  </div>
                ))}
              </div>
              {/* Legende — Pflicht bei 2 Serien (und Kontrast-Auflage der Palette) */}
              <div className="flex gap-4 mt-2">
                {['inbound', 'outbound'].map((dir) => (
                  <span key={dir} className={`flex items-center gap-1.5 text-xs ${textMain}`}>
                    <span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: SERIES[dir][mode] }} />
                    {t(dir === 'inbound' ? 'einvoicing.inbound' : 'einvoicing.outbound')}
                  </span>
                ))}
              </div>
            </>
          )}
        </Card>

        {/* Status-Funnel Eingang */}
        <Card isDarkMode={isDarkMode} className="min-w-0">
          <SectionTitle isDarkMode={isDarkMode}>{t('einvoicing.dashFunnel')}</SectionTitle>
          <div className="space-y-3">
            {funnel.map((f) => (
              <div key={f.key}>
                <div className="flex justify-between text-xs mb-1">
                  <span className={textMain}>{f.label}</span>
                  <span className={`tabular-nums font-medium ${textMain}`}>{f.n}</span>
                </div>
                <div className={`h-2.5 rounded-full overflow-hidden ${isDarkMode ? 'bg-white/10' : 'bg-slate-100'}`}>
                  <div className={`h-full rounded-full ${funnelBar[f.tone]}`} style={{ width: `${(f.n / funnelMax) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>

          {/* Regime-Abdeckung (SAP-DRC-Laenderkacheln, reduziert) */}
          <SectionTitle isDarkMode={isDarkMode}><span className="block mt-5">{t('einvoicing.dashRegimes')}</span></SectionTitle>
          <div className="flex flex-wrap gap-2">
            {(regimes || []).map((r) => {
              const n = (stats?.byRegime || []).filter((x) => x.regime === r.key).reduce((s, x) => s + Number(x.n), 0);
              return (
                <span key={r.key} title={`${r.country} — ${r.model}${r.verified ? '' : ' · ' + t('einvoicing.regimeUnverified')}`}
                  className={`text-xs px-2.5 py-1.5 rounded-xl border ${isDarkMode ? 'border-white/[0.12] bg-white/[0.06] text-white/70' : 'border-slate-200 bg-slate-50 text-slate-600'}`}>
                  {r.flag} {r.key.toUpperCase()} <span className="tabular-nums font-semibold">{n}</span>
                  {!r.verified && <span className="ml-1 opacity-60">⚠</span>}
                </span>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Letzte Aktivitaet */}
      <Card isDarkMode={isDarkMode}>
        <SectionTitle isDarkMode={isDarkMode}>{t('einvoicing.dashRecent')}</SectionTitle>
        {(stats?.recent || []).length === 0 ? (
          <p className={`text-sm ${labelMuted}`}>{t('einvoicing.dashEmpty')}</p>
        ) : (
          <div className="divide-y divide-slate-200/20">
            {(stats?.recent || []).map((r) => (
              <button key={r.id} onClick={() => onOpenInvoice(r.id)}
                className={`w-full flex items-start gap-3 py-2.5 px-1 text-left rounded-lg transition-colors ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                {r.direction === 'inbound'
                  ? <Inbox className={`w-4 h-4 mt-0.5 flex-shrink-0 ${labelMuted}`} />
                  : <Send className={`w-4 h-4 mt-0.5 flex-shrink-0 ${labelMuted}`} />}
                <div className="flex-1 min-w-0">
                  {/* Volle Rechnungsnummer auf eigener Zeile ueber die ganze Breite — wird nicht
                      mehr vom Betrag/Chip verdraengt */}
                  <div className={`text-sm font-medium truncate ${textMain}`}>{r.invoice_number || t('einvoicing.noNumber')}</div>
                  {/* Zeile 2: Partner+Datum links, Betrag ueber Status-Chip rechts */}
                  <div className="flex items-start justify-between gap-3 mt-0.5">
                    <span className={`text-xs truncate min-w-0 ${labelMuted}`}>{r.counterparty_name} · {fmtDate(r.created_at)}</span>
                    <span className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className={`text-sm font-semibold tabular-nums whitespace-nowrap ${textMain}`}>{money(r.total_payable, r.currency)}</span>
                      <BusinessChip state={r.state} isDarkMode={isDarkMode} t={t} />
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default DashboardScreen;
